import {
  createSelector,
  createSlice,
  PayloadAction,
  WritableDraft,
} from '@reduxjs/toolkit'

import {
  DATES,
  REFERRAL_IMPORT_DIRECTION,
  REFERRAL_SOURCE,
} from '@/lib/constants'
import { ClientDetailsFormValues } from '@/pages/referClient/steps/1_ClientDetails/types'
import { RootState } from '@/redux/store'
import { HydraMember } from '@/types/api'
import {
  CompanyEntity,
  ReferralStatusEntity,
  UserEntity,
} from '@/types/entities'
import {
  FormErrors,
  ReferralImportDirection,
  ReferralIndividual,
  ReferralSource,
} from '@/types/misc'
import { ClientItem } from '@/types/responses/clients'
import { CompanyUserItem } from '@/types/responses/company-users'
import { SessionMetadataCompany } from '@/types/responses/session-metadata'

// @todo: Move all the form values and validation logic to Formik to reduce
// the amount of state we need to manage manually
export interface LogHistoricalReferralState {
  currentCompanyUserId: number | null
  isModalOpen: boolean
  isUserValid: boolean | null
  isClientValid: boolean | null
  isSourceValid: boolean | null
  isNewClientFormValid: boolean | null
  isCompanyAdvisorValid: boolean | null
  isServiceAreaValid: boolean | null
  isStatusValid: boolean | null
  isToCompanyUsersValid: boolean | null
  isValid: boolean | null
  isSelectingUser: boolean
  logStatus: 'invalid' | 'ready' | 'processing' | 'imported' | 'error'
  logErrors?: FormErrors
  logData: LogHistoricalReferralData
  selectedUser: CompanyUserItem | null
  // data used for state restarts
  selectedUserInit: CompanyUserItem | null
}

export interface LogHistoricalReferralData {
  circumstances: string
  otherCompany: Pick<CompanyEntity, 'id' | 'presentationName'> | null
  otherCompanyUser: Pick<
    UserEntity,
    'id' | 'firstName' | 'lastName' | 'email'
  > | null
  otherCompanyUserDefaultUsed: boolean
  inviteAdvisor: boolean
  advisorEmail: string
  advisorEmailValid: boolean
  existingClient: ClientItem | null
  newClientDateOfBirth: string
  newClientEmail: string
  newClientEmailValid: boolean
  newClientFirstName: string
  newClientLastName: string
  newClientTelephone: string
  newServiceAreaId: number | null
  dateReferred: string
  serviceAreaId: number
  referralStatus: Pick<
    ReferralStatusEntity,
    'id' | 'name' | 'stage' | 'code'
  > | null
  referralDirection: ReferralImportDirection
  interceptorLogId: number | null
  interceptorGuessedServiceAreaIds: number[] | null
  referralSource: ReferralSource | null
  directLeadSource: string
  selectedToCompanyUsers: ReferralIndividual[]
  shouldNotifyToCompanyUsers: boolean
}

const INITIAL_STATE: LogHistoricalReferralState = {
  currentCompanyUserId: null,
  isModalOpen: false,
  isUserValid: null,
  isClientValid: null,
  isSourceValid: null,
  isNewClientFormValid: null,
  isCompanyAdvisorValid: null,
  isServiceAreaValid: null,
  isStatusValid: null,
  isToCompanyUsersValid: null,
  isValid: null,
  isSelectingUser: false,
  logStatus: 'invalid',
  logErrors: undefined,
  logData: {
    circumstances: '',
    otherCompany: null,
    otherCompanyUser: null,
    otherCompanyUserDefaultUsed: false,
    inviteAdvisor: false,
    advisorEmail: '',
    advisorEmailValid: true,
    existingClient: null,
    newClientDateOfBirth: '',
    newClientEmail: '',
    newClientEmailValid: false,
    newClientFirstName: '',
    newClientLastName: '',
    newClientTelephone: '',
    newServiceAreaId: null,
    dateReferred: DATES.endOfToday.toISOString(),
    serviceAreaId: -1,
    referralStatus: null,
    referralDirection: REFERRAL_IMPORT_DIRECTION.FROM,
    interceptorLogId: null,
    interceptorGuessedServiceAreaIds: null,
    referralSource: null,
    directLeadSource: '',
    selectedToCompanyUsers: [],
    shouldNotifyToCompanyUsers: true,
  },
  selectedUser: null,
  selectedUserInit: null,
}

function assignSessionResetState(
  state: WritableDraft<LogHistoricalReferralState>,
  isModalOpen: boolean,
) {
  // - keep legged in user information though
  Object.assign(state, {
    ...INITIAL_STATE,
    isModalOpen,
    currentCompanyUserId: state.currentCompanyUserId,
    selectedUser: { ...state.selectedUserInit },
    selectedUserInit: state.selectedUserInit,
  })
  setValidationState(state)
}

const logHistoricalReferral = createSlice({
  name: 'logHistoricalReferral',
  initialState: INITIAL_STATE,
  reducers: {
    resetLogReferralState(state) {
      assignSessionResetState(state, false)
    },

    initializeState(
      state,
      action: {
        payload: {
          currentUser: HydraMember<Pick<UserEntity, 'id' | 'fullName'>>
          currentCompanyUserId: number
          currentCompany: SessionMetadataCompany
        }
      },
    ) {
      state.currentCompanyUserId = action.payload.currentCompanyUserId
      state.selectedUser = {
        '@id': state.currentCompanyUserId.toString(),
        '@type': '',
        '@context': '',
        id: state.currentCompanyUserId,
        user: action.payload.currentUser,
        company: action.payload.currentCompany,
      }
      state.selectedUserInit = { ...state.selectedUser }

      setValidationState(state)
    },

    setSelectedUser(
      state,
      action: {
        payload: LogHistoricalReferralState['selectedUser']
      },
    ) {
      state.selectedUser = action.payload
      setValidationState(state)
    },

    setErrors(
      state,
      action: PayloadAction<LogHistoricalReferralState['logErrors']>,
    ) {
      state.logErrors = action.payload
      state.logStatus = 'error'
    },

    setIsNewClientFormValid(
      state,
      action: { payload: LogHistoricalReferralState['isNewClientFormValid'] },
    ) {
      state.isNewClientFormValid = action.payload
      setValidationState(state)
    },

    showLogHistoricalReferralModal(state) {
      assignSessionResetState(state, true)
    },

    hideLogHistoricalReferralModal(state) {
      assignSessionResetState(state, false)
    },

    setIsLogHistoricalReferralModalOpen(
      state,
      action: { payload: LogHistoricalReferralState['isModalOpen'] },
    ) {
      assignSessionResetState(state, action.payload)
    },

    setIsSelectingUser(
      state,
      action: { payload: LogHistoricalReferralState['isSelectingUser'] },
    ) {
      state.isSelectingUser = action.payload
    },

    setNewClientData(state, action: PayloadAction<ClientDetailsFormValues>) {
      state.logData.newClientDateOfBirth = action.payload.dateOfBirth || ''
      state.logData.newClientEmail = action.payload.email
      state.logData.newClientEmailValid = true
      state.logData.newClientFirstName = action.payload.firstName || ''
      state.logData.newClientLastName = action.payload.lastName || ''
      state.logData.newClientTelephone = action.payload.telephone || ''
      setValidationState(state)
    },

    updateLogData(
      state,
      action: PayloadAction<Partial<LogHistoricalReferralData>>,
    ) {
      state.logData = {
        ...state.logData,
        ...action.payload,
      }
      setValidationState(state)
    },

    clearReferralSource() {
      updateLogData({ referralSource: null })
    },
  },
})

// @todo: Move validation logic to Formik & Yup to reduce the amount of state
// we need to manage manually
function setValidationState(state: WritableDraft<LogHistoricalReferralState>) {
  const isDirectLeadReferral =
    state.logData.referralSource === REFERRAL_SOURCE.DIRECT

  // validate user
  state.isUserValid = !!state.selectedUser?.company && !!state.selectedUser.user

  // validate client
  if (state.logData.existingClient) {
    state.isClientValid = true
  } else {
    state.isClientValid = state.isNewClientFormValid
  }

  // validate source
  state.isSourceValid = !!state.logData.referralSource

  // validate company adviser
  state.isCompanyAdvisorValid =
    (!!state.logData.advisorEmail && state.logData.advisorEmailValid) ||
    !!state.logData.otherCompany ||
    isDirectLeadReferral

  // validate service area
  state.isServiceAreaValid = state.logData.serviceAreaId > -1

  state.isStatusValid = !!state.logData.referralStatus

  state.isToCompanyUsersValid = isDirectLeadReferral
    ? state.logData.selectedToCompanyUsers.length > 0
    : true

  state.isValid =
    state.isUserValid &&
    state.isClientValid &&
    state.isSourceValid &&
    state.isCompanyAdvisorValid &&
    state.isServiceAreaValid &&
    state.isStatusValid &&
    state.isToCompanyUsersValid

  // validate isSelectingUser
  // ensure that isSelectingUser is true if selected user is different then current one
  if (
    !state.isSelectingUser &&
    state.currentCompanyUserId != state.selectedUser?.id
  ) {
    state.isSelectingUser = true
  }

  return state
}

export const logHistoricalReferralReducer = logHistoricalReferral.reducer

export const selectLogHistoricalReferralState = (state: RootState) =>
  state.logReferral

export const selectLogData = createSelector(
  selectLogHistoricalReferralState,
  (state) => state.logData,
)

export const selectReferralSource = createSelector(
  selectLogData,
  (logData) => logData.referralSource,
)

export const selectIsLoggingSentReferral = createSelector(
  selectLogData,
  (logData) => logData.referralDirection === REFERRAL_IMPORT_DIRECTION.TO,
)

export const selectIsLoggingReceivedReferral = createSelector(
  selectLogData,
  (logData) => logData.referralDirection === REFERRAL_IMPORT_DIRECTION.FROM,
)

export const selectIsLoggingDirectLeadReferral = createSelector(
  selectReferralSource,
  (referralSource) => referralSource === REFERRAL_SOURCE.DIRECT,
)

export const {
  resetLogReferralState,
  setSelectedUser,
  setErrors,
  setNewClientData,
  setIsNewClientFormValid,
  setIsLogHistoricalReferralModalOpen,
  showLogHistoricalReferralModal,
  hideLogHistoricalReferralModal,
  setIsSelectingUser,
  initializeState,
  updateLogData,
  clearReferralSource,
} = logHistoricalReferral.actions
